import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Film, Star, Trash2, AlertCircle, ArrowLeft, Sparkles, Popcorn } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getWatchedMovies, removeFromWatched } from '../../lib/supabase';
import { MovieListControls } from '../ui/MovieListControls';
import { sortMovies, paginateMovies } from '../../lib/sorting';
import type { MovieListConfig } from '../../lib/types';

interface WatchedMovie {
  id: string;
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
  created_at: string;
  watched_at: string;
  rating?: number;
  review?: string;
}

export function WatchedMoviesPage() {
  const navigate = useNavigate();
  const [movies, setMovies] = useState<WatchedMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [listConfig, setListConfig] = useState<MovieListConfig>({
    sort: { field: 'added', order: 'desc' },
    pagination: { page: 1, perPage: 10, total: 0 }
  });

  useEffect(() => {
    loadWatchedMovies();
  }, []);

  const loadWatchedMovies = async () => {
    try {
      setLoading(true);
      setError(null);
      const watchedList = await getWatchedMovies();
      setMovies(watchedList);
      setListConfig(prev => ({
        ...prev,
        pagination: { ...prev.pagination, total: watchedList.length }
      }));
    } catch (err) {
      console.error('Error loading watched movies:', err);
      setError('Failed to load watched movies');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (tmdbId: number) => {
    try {
      setRemovingId(tmdbId);
      await removeFromWatched(tmdbId);
      setMovies(prev => {
        const updated = prev.filter(movie => movie.tmdb_id !== tmdbId);
        setListConfig(config => ({
          ...config,
          pagination: {
            ...config.pagination,
            total: updated.length,
            page: Math.min(
              config.pagination.page,
              Math.ceil(updated.length / config.pagination.perPage)
            )
          }
        }));
        return updated;
      });
    } catch (err) {
      console.error('Error removing from watched movies:', err);
      setError('Failed to remove movie from watched list');
    } finally {
      setRemovingId(null);
    }
  };

  const renderRating = (rating?: number) => {
    if (!rating) return null;
    return (
      <div className="mt-1 flex items-center gap-1">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={cn(
              'h-4 w-4',
              index < rating ? 'fill-primary text-primary' : 'text-white/20'
            )}
          />
        ))}
      </div>
    );
  };

  const sortedMovies = sortMovies(movies, listConfig.sort);
  const paginatedMovies = paginateMovies(
    sortedMovies,
    listConfig.pagination.page,
    listConfig.pagination.perPage
  );

  if (loading) {
    return (
      <div className="grain flex min-h-screen items-center justify-center">
        <div className="h-1 w-40 overflow-hidden rounded-full bg-secondary">
          <div className="animate-progress h-full rounded-full bg-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="btn-quiet p-2"
              aria-label="Back to home"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="display flex items-center gap-2.5 whitespace-nowrap text-xl font-semibold sm:text-2xl">
              <span
                className="hidden h-8 w-8 items-center justify-center rounded-full text-white sm:flex"
                style={{ background: 'linear-gradient(180deg, hsl(358 78% 54%), hsl(352 80% 40%))', boxShadow: '0 0 20px hsl(358 80% 50% / 0.25)' }}
              >
                <Film className="h-4 w-4" />
              </span>
              Watched Movies
            </h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <button
              onClick={() => navigate('/conversation')}
              className="btn-quiet px-2.5 py-2 text-sm"
              aria-label="Get Recommendations"
            >
              <Sparkles className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">Get Recommendations</span>
            </button>
            <button
              onClick={() => navigate('/watchlist')}
              className="btn-quiet px-2.5 py-2 text-sm"
              aria-label="View Watchlist"
            >
              <Popcorn className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">View Watchlist</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 md:py-12">
        {error && (
          <div className="mb-8 flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/10 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {movies.length > 0 ? (
          <>
            <MovieListControls
              sort={listConfig.sort}
              pagination={listConfig.pagination}
              onSortChange={(field, order) => setListConfig(prev => ({
                ...prev,
                sort: { field, order }
              }))}
              onPageChange={page => setListConfig(prev => ({
                ...prev,
                pagination: { ...prev.pagination, page }
              }))}
              className="mb-5"
            />

            <div className="space-y-3.5">
              {paginatedMovies.map((movie, i) => (
                <div
                  key={movie.id}
                  className="panel-raised accent-frame animate-rise flex gap-5 p-5"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="poster-frame h-36 w-24 flex-shrink-0 shadow-poster">
                    {movie.poster_url ? (
                      <img
                        src={movie.poster_url}
                        alt={movie.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Film className="h-8 w-8 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="display text-lg font-semibold leading-snug sm:text-xl">
                          {movie.title}
                        </h2>
                        <p className="text-sm text-muted-foreground">{movie.year}</p>
                        {renderRating(movie.rating)}
                      </div>
                      <button
                        onClick={() => handleRemove(movie.tmdb_id)}
                        disabled={removingId === movie.tmdb_id}
                        aria-label={`Remove ${movie.title} from watched list`}
                        className={cn(
                          'shrink-0 rounded-full border border-white/10 bg-white/[0.03] p-2.5 text-muted-foreground',
                          'transition-all duration-200 hover:border-destructive/40 hover:text-red-400',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive',
                          removingId === movie.tmdb_id && 'cursor-not-allowed opacity-50'
                        )}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground sm:line-clamp-3">
                      {movie.overview}
                    </p>
                    {movie.review && (
                      <div className="mt-3 rounded-xl border border-accent/20 bg-accent/[0.07] p-3.5">
                        <p className="text-sm leading-relaxed text-foreground/90">
                          <span className="font-semibold text-accent">Your Review: </span>
                          {movie.review}
                        </p>
                      </div>
                    )}
                    <p className="mt-2.5 text-xs tracking-wide text-muted-foreground/60">
                      Watched {new Date(movie.watched_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="panel mx-auto max-w-lg px-8 py-14 text-center">
            <div className="mx-auto mb-5 inline-flex rounded-2xl border border-accent/25 bg-accent/10 p-4 text-accent">
              <Film className="h-8 w-8" />
            </div>
            <h2 className="display mb-2 text-2xl font-semibold">
              No watched movies yet
            </h2>
            <p className="mx-auto mb-7 max-w-sm leading-relaxed text-muted-foreground">
              Start marking movies as watched to keep track of what you've seen.
            </p>
            <button
              onClick={() => navigate('/conversation')}
              className="btn-marquee px-7 py-3"
            >
              <Sparkles className="h-5 w-5" />
              Get Movie Recommendations
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
